// CompensationFinancialSection.jsx
import React, { useState } from 'react';
import { Box, Typography, Grid, Paper, Chip, Divider, ToggleButtonGroup, ToggleButton } from '@mui/material';
import RevenueGenerationChart from '../visualizations/RevenueGenerationChart';
import BillingRateChart from '../visualizations/BillingRateChart';
import MarginPercentageChart from '../visualizations/MarginPercentageChart';
import { Logger } from '../../utils/logger';

const logger = new Logger('CompensationFinancialSection');

const REASON_COLORS = {
  'Promotion':         '#03DAC6',
  'Performance Based': '#BB86FC',
  'Annual Review':     '#FFCF44',
};

const fmt = (value, style = 'currency') => {
  if (value == null || value === 0) return 'N/A';
  if (style === 'currency') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'USD',
      minimumFractionDigits: 0, maximumFractionDigits: 0,
    }).format(value);
  }
  return value;
};

const fmtDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
};

const CompensationFinancialSection = ({
  salaryHistory = [],
  totalCompensation = {},
  financialSummary = {},
  compensationProgression = {},
  employee = null,
}) => {
  const [timeFrame, setTimeFrame] = useState('current_year');

  const safe    = totalCompensation   || {};
  const summary = financialSummary    || {};
  const prog    = compensationProgression || {};

  const topStats = [
    { label: 'Base Salary',          value: fmt(safe.baseSalary),                                                             color: '#BB86FC' },
    { label: 'Total Cost',           value: fmt(safe.annualCost),                                                             color: '#CF6679' },
    { label: 'Lifetime Revenue',     value: fmt(summary.total_lifetime_revenue),                                              color: '#03DAC6' },
    { label: 'Est. Annual Revenue',  value: fmt(summary.estimated_annual_revenue),                                            color: '#03DAC6' },
    { label: 'ROI',                  value: summary.roi_metrics?.roi_percentage != null ? `${summary.roi_metrics.roi_percentage.toFixed(1)}%` : 'N/A', color: '#FFCF44' },
    { label: 'Revenue / $1 Cost',    value: summary.billing_efficiency?.revenue_per_cost_dollar != null ? `$${summary.billing_efficiency.revenue_per_cost_dollar.toFixed(2)}` : 'N/A', color: '#64B5F6' },
  ];

  const growthStats = [
    { label: 'Salary Growth Rate',         value: prog.salary_growth_rate != null ? `+${prog.salary_growth_rate.toFixed(1)}%` : 'N/A',  color: '#03DAC6' },
    { label: 'Total Increases',            value: prog.total_salary_changes ?? 'N/A',                                                    color: '#BB86FC' },
    { label: 'Performance-Based',          value: prog.performance_based_increases ?? 'N/A',                                             color: '#FFCF44' },
    { label: 'Most Recent Change',         value: fmtDate(prog.most_recent_change),                                                      color: '#64B5F6' },
    { label: 'Annual Profit Contribution', value: fmt(summary.estimated_annual_revenue && safe.annualCost ? summary.estimated_annual_revenue - safe.annualCost : null), color: '#03DAC6' },
  ];

  return (
    <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>Financials</Typography>

      {/* Row 1 — Key financial stats */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        {topStats.map(stat => (
          <Box
            key={stat.label}
            sx={{
              flex: 1, minWidth: 110,
              bgcolor: 'background.default',
              border: '1px solid', borderColor: 'divider',
              borderRadius: 1.5, p: 1.5,
            }}
          >
            <Typography sx={{
              color: 'text.secondary', fontSize: '0.65rem',
              textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.75,
            }}>
              {stat.label}
            </Typography>
            <Typography variant="h6" sx={{ color: stat.color, fontWeight: 700, lineHeight: 1 }}>
              {stat.value}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Row 2 — Compensation history + growth */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {/* Salary history table */}
        <Grid item xs={12} md={8} sx={{ display: 'flex', flexDirection: 'column' }}>
          <Paper elevation={0} sx={{
            p: 2, bgcolor: 'background.default',
            border: '1px solid', borderColor: 'divider', flex: 1,
          }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1.5 }}>
              Compensation History
            </Typography>

            {salaryHistory.length > 0 ? (
              <>
                <Grid container sx={{ px: 1, mb: 0.5 }}>
                  {['Date', 'New Salary', 'Change', 'Reason', 'Perf Score'].map((h, i) => (
                    <Grid item xs={i === 3 ? 3 : 2} key={h}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        {h}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
                <Divider sx={{ mb: 0.5 }} />
                {salaryHistory.map((entry, i) => {
                  const reasonColor = REASON_COLORS[entry.change_reason] || '#64B5F6';
                  return (
                    <Grid
                      container
                      key={i}
                      sx={{
                        py: 1.25, px: 1, alignItems: 'center',
                        borderBottom: '1px solid rgba(255,255,255,0.06)',
                      }}
                    >
                      <Grid item xs={2}>
                        <Typography variant="body2">{fmtDate(entry.effective_date)}</Typography>
                      </Grid>
                      <Grid item xs={2}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {fmt(entry.new_salary)}
                        </Typography>
                      </Grid>
                      <Grid item xs={2}>
                        <Typography variant="body2" sx={{ color: '#03DAC6', fontWeight: 600 }}>
                          +{entry.change_percentage?.toFixed(1)}%
                        </Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Chip
                          label={entry.change_reason}
                          size="small"
                          sx={{
                            height: 20, fontSize: '0.65rem',
                            bgcolor: `${reasonColor}22`,
                            color: reasonColor,
                            border: `1px solid ${reasonColor}`,
                            borderRadius: '4px',
                            '& .MuiChip-label': { px: 1 },
                          }}
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <Typography variant="body2" color="text.secondary">
                          {entry.performance_score != null ? entry.performance_score : '—'}
                        </Typography>
                      </Grid>
                    </Grid>
                  );
                })}
              </>
            ) : (
              <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 5 }}>
                No compensation history available
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Compensation growth stats */}
        <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column' }}>
          <Paper elevation={0} sx={{
            p: 2, bgcolor: 'background.default',
            border: '1px solid', borderColor: 'divider', flex: 1,
          }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1.5 }}>
              Compensation Growth
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              {growthStats.map(item => (
                <Box
                  key={item.label}
                  sx={{
                    bgcolor: 'background.paper', borderRadius: 1.5,
                    p: 1.5, border: '1px solid', borderColor: 'divider',
                  }}
                >
                  <Typography sx={{
                    fontSize: '0.65rem', textTransform: 'uppercase',
                    letterSpacing: '0.06em', color: 'text.secondary', mb: 0.5,
                  }}>
                    {item.label}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: item.color, lineHeight: 1 }}>
                    {item.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Row 3 — Revenue / Billing Rate / Margin charts */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <ToggleButtonGroup
          size="small"
          value={timeFrame}
          exclusive
          onChange={(_, v) => { if (v) setTimeFrame(v); }}
          aria-label="time frame"
        >
          <ToggleButton value="current_year">Current Year</ToggleButton>
          <ToggleButton value="last_4_quarters">Last 4 Quarters</ToggleButton>
          <ToggleButton value="last_year">Last Year</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Paper elevation={0} sx={{
        p: 1.5, bgcolor: 'background.default',
        border: '1px solid', borderColor: 'divider',
      }}>
        <Grid container spacing={1}>
          <Grid item xs={12} md={4}>
            <RevenueGenerationChart
              employee={{ employee_id: employee?.employee_id }}
              compact hideToggle timeFrame={timeFrame}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <BillingRateChart
              employee={{ employee_id: employee?.employee_id }}
              compact hideToggle timeFrame={timeFrame}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <MarginPercentageChart
              employee={{ employee_id: employee?.employee_id }}
              compact hideToggle timeFrame={timeFrame}
            />
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default CompensationFinancialSection;
